import { AbstractControl, FormControl, Validators, FormArray, FormGroup } from '@angular/forms';
import { User } from './user.schema';
import { Delivery, toDate } from './delivery.schema';
import { Vehicle } from './vehicle.schema';
import { phoneValid } from '../helpers/validators/phone.validator';
import { ExpertServiceSchema } from './expert-service.schems';
import { requiredIfAllEmpty } from '../helpers/validators/required-if-all-empty.validator';
import { Transform } from 'class-transformer';
import { EnumAux } from '../helpers/enum-aux.helper';
import { Partner } from './partner.schema';

export enum SalaryFrequency {
    monthly = "monthly",
    weekly = "weekly",
}

export class SalaryFrequencyAux extends EnumAux {
    static aux = {
        monthly: { css: "", ln: { it: "mensile" } },
        weekly: { css: "", ln: { it: "settimanale" } },
    }
}

export class Expert {
    public static validation(vehicles: Vehicle[], profile = false): { [key: string]: AbstractControl } {
        const decimalPattern = /^\d+(\.\d{1,2})?$/;
        let validator: { [key: string]: AbstractControl } = {
            phone: new FormControl('', Validators.compose([Validators.required, phoneValid()])),
            address: new FormControl('', Validators.required),
            longitude: new FormControl(''),
            latitude: new FormControl(''),
            notes: new FormControl(''),
            vehicles: new FormArray(vehicles.map(x => new FormControl(false))),
            minimumKmIncluded: new FormControl(null, [Validators.pattern(decimalPattern)]),
            expertServices: new FormArray([new FormGroup(ExpertServices.validation())]),
            expertProvinces: new FormArray([new FormGroup(ExpertProvinces.validation())]),
            // teamLeaderProvince: new FormGroup({ id: new FormControl('') }),
            expertTeamLeaderProvinces: new FormArray([new FormGroup(ExpertTeamLeaderProvinces.validation())]),
            isTeamLeader: new FormControl(''),
            receiveEmailMsg: new FormControl(true),
            receiveWhatsappMsg: new FormControl(true),
            extraOutSideCityKmPrice: new FormControl(null, [Validators.pattern(/^\d+(\.\d{1,2})?$/)]),
            isVatCode: new FormControl(false),
            vatCode: new FormControl(''),
            fiscalCode: new FormControl(''),
            birthPlace: new FormControl(''),
            dateOfBirth: new FormControl('', [this.dateOfBirthValidator(18, 120)]),
            bankAccountData: new FormControl('', [
                // Validators.required,
                Validators.pattern(/^[A-Z0-9]{15,34}$/i)  // IBAN: 15–34 alphanumeric
            ]),
            holdingPercentage: new FormControl(0, [Validators.pattern(/^\d+(\.\d{1,2})?$/)]),
            salaryFrequency: new FormControl('', Validators.required),
            weeklyDepositLimit: new FormControl('', [Validators.pattern(/^\d+(\.\d{1,2})?$/)]),
            teamLeaderPartners: new FormArray([new FormGroup(TeamLeaderPartners.validation())]),
            //expertIndentityCode: new FormControl(''),
        };
        validator['user'] = profile ?
            new FormGroup({ ...User.specificValidation(), ...User.editLoginValidation() }) :
            new FormGroup(User.specificValidation());

        validator['vatCode'].setValidators([
            Validators.pattern(/^\d{11}$/),
            requiredIfAllEmpty({ value: [validator['fiscalCode']] })
        ]);
        validator['fiscalCode'].setValidators([
            Validators.pattern(/^\w{6}\d{2}\w\d{2}\w\d{3}\w$/),
            requiredIfAllEmpty({ value: [validator['vatCode']] })
        ]);
        return validator;
    }

    public static readonly validationMessages = {
        'phone': [
            { type: 'required', message: 'Obbligatorio' },
            { type: 'phone', message: 'Formato invalido. Deve contenere fra le 10 e le 14 cifre' }
        ],
        'address': [
            { type: 'required', message: 'Obbligatorio' }
        ],
        'user': User.validationMessages,
        'expertServices': {
            'service': {
                'id': [
                    { type: 'required', message: 'Obbligatorio' }
                ],
                'salary': [
                    { type: 'pattern', message: 'Deve essere un numero valido' }
                ],
                'minimumKmPrice': [
                    { type: 'pattern', message: 'Deve essere un numero valido' }
                ],
                'salaryPerItem': [
                    { type: 'pattern', message: 'Deve essere un numero valido' }
                ],
            },
        },
        'extraOutSideCityKmPrice': [
            { type: 'pattern', message: 'Deve essere un numero valido' }
        ],
        'minimumKmIncluded': [
            { type: 'pattern', message: 'Deve essere un numero valido' }
        ],
        'vatCode': [
            { type: 'required', message: 'Obbligatoria' },
            { type: 'pattern', message: 'Formato errato' },
            { type: 'requiredIfAllEmpty', message: 'Codice fiscale o partita iva obbligatori' }
        ],
        'fiscalCode': [
            { type: 'required', message: 'Obbligatorio' },
            { type: 'pattern', message: 'Formato errato' },
            { type: 'requiredIfAllEmpty', message: 'Codice fiscale o partita iva obbligatori' }
        ],
        'dateOfBirth': [
            { type: 'required', message: 'Obbligatoria' },
            { type: 'futureDate', message: 'La data non può essere nel futuro' },
            { type: 'minAge', message: 'Devi avere almeno 18 anni' },
            { type: 'maxAge', message: 'Età non valida (troppo elevata)' }
        ],
        'bankAccountData': [
            { type: 'required', message: 'Obbligatorio' },
            { type: 'pattern', message: 'Formato IBAN non valido (15–34 caratteri alfanumerici)' }
        ],
        'holdingPercentage': [
            { type: 'required', message: 'Obbligatorio' },
            { type: 'pattern', message: 'Deve essere un numero valido' }
        ],
        'weeklyDepositLimit': [
            { type: 'required', message: 'Obbligatorio' },
            { type: 'pattern', message: 'Deve essere un numero valido' }
        ],
    };

    public static serviceFormGroup() {
        return new FormGroup(ExpertServices.validation());
    }

    
    public static provinceFormGroup() {
        return new FormGroup(ExpertProvinces.validation());
    }

    public static teamLeaderProvinceFormGroup() {
        return new FormGroup(ExpertTeamLeaderProvinces.validation());
    }

    public static teamLeaderPartnerFormGroup() {
        return new FormGroup(TeamLeaderPartners.validation());
    }

    public static dateOfBirthValidator(minAge = 18, maxAge = 120) {
        return (control: AbstractControl): { [key: string]: any } | null => {
            if (!control.value) return null;

            const inputDate = new Date(control.value);
            const today = new Date();

            // Future date check
            if (inputDate > today) {
            return { futureDate: true };
            }

            const age = today.getFullYear() - inputDate.getFullYear();
            const monthDiff = today.getMonth() - inputDate.getMonth();
            const dayDiff = today.getDate() - inputDate.getDate();

            const hasBirthdayPassed = monthDiff > 0 || (monthDiff === 0 && dayDiff >= 0);
            const actualAge = hasBirthdayPassed ? age : age - 1;

            if (actualAge < minAge) {
            return { minAge: { requiredAge: minAge, actualAge } };
            }

            if (actualAge > maxAge) {
            return { maxAge: { allowedMaxAge: maxAge, actualAge } };
            }

            return null;
        };
    }



    constructor(
        public id?: number,
        public phone?: string,
        public address?: string,
        public longitude?: string,
        public latitude?: string,
        public notes?: string,
        public vehicles?: Vehicle[],
        public user?: User,
        public deliveries?: Delivery[],
        public minimumKmIncluded?: number,
        public isTeamLeader?: boolean,
        public expertTeamLeaderProvinces?: ExpertTeamLeaderProvinces[],
        public expertServices?: ExpertServices[],
        public expertProvinces?: ExpertProvinces[],
        public receiveEmailMsg?: boolean,
        public receiveWhatsappMsg?: boolean,
        public extraOutSideCityKmPrice?: number,
        public isVatCode?: boolean,
        public vatCode?: string,
        public fiscalCode?: string,
        public birthPlace?: string,
        dateOfBirth?: Date,
        public bankAccountData?: string,
        public holdingPercentage?: number,
        public salaryFrequency?: SalaryFrequency,
        public weeklyDepositLimit?: number,
        public teamLeaderPartners?: any,
        public contract?: {
            id: number,
            contractUrl: string,
            signedContractUrl: string
        },
        //public expertIndentityCode?: string,
    ) { 
        this.dateOfBirth = dateOfBirth;
    }
    @Transform(toDate, { toClassOnly: true }) public dateOfBirth?: Date;
}

export class ExpertServices {
    public static validation(): { [key: string]: AbstractControl } {
        let validator: { [key: string]: AbstractControl } = {
            service: this.serviceFormGroup(),
        };
        return validator;
    }

    public static serviceFormGroup() {
        const decimalPattern = /^\d+(\.\d{1,2})?$/;
        return new FormGroup({
            id: new FormControl(null),
            salary: new FormControl(null, [Validators.pattern(decimalPattern)]),
            minimumKmPrice: new FormControl(null, [Validators.pattern(decimalPattern)]),
            salaryPerItem: new FormControl(null, [Validators.pattern(decimalPattern)]),
            // extraOutSideCityKmPrice: new FormControl(null, [Validators.pattern(decimalPattern)]),
        });
    }

    constructor(
        public id?: number,
        public salary?: number,
        public minimumKmPrice?: string,
        public salaryPerItem?: string,
        // public extraOutSideCityKmPrice?: number,
        public service?: ExpertServiceSchema,
        public updatedAt?: Date,
        public createdAt?: Date,
    ) { }
}

export class ExpertProvinces {
    public static validation(): { [key: string]: AbstractControl } {
        let validator: { [key: string]: AbstractControl } = {
            province: this.provinceFormGroup(),
        };
        return validator;
    }

    public static provinceFormGroup() {
        return new FormGroup({
            id: new FormControl(null),
        });
    }

    constructor(
        public id?: number,
        public provinceCode?: string,
        public province?: any,
        public updatedAt?: Date,
        public createdAt?: Date,
    ) { }
}
export class ExpertTeamLeaderProvinces {
    public static validation(): { [key: string]: AbstractControl } {
        let validator: { [key: string]: AbstractControl } = {
            province: this.provinceFormGroup(),
        };
        return validator;
    }

    public static provinceFormGroup() {
        return new FormGroup({
            id: new FormControl(null),
        });
    }

    constructor(
        public id?: number,
        public provinceCode?: string,
        public province?: any,
        public updatedAt?: Date,
        public createdAt?: Date,
    ) { }
}

export class TeamLeaderPartners {
    public static validation(): { [key: string]: AbstractControl } {
        let validator: { [key: string]: AbstractControl } = {
            partner: this.teamLeaderPartnerFormGroup(),
        };
        return validator;
    }

    public static teamLeaderPartnerFormGroup() {
        return new FormGroup({
            id: new FormControl(''),
        });
    }

    constructor(
        public id?: number,
        public partner?: Partner,
    ) { }
}